// ===== ./src/utils/layoutVideo.ts =====
// NOTE: Mirrors layoutReel.ts
// Any updates here should also be reviewed in layoutReel.ts to keep behavior consistent.

import { normalizeColor } from "./color";

export interface VideoFrame {
  left: string;
  top: string;
  width: string;
  height: string;
  [key: string]: string;
}

export interface VideoLayoutResult {
  frame: VideoFrame;
  textPanel: VideoFrame;
  canvasBox: VideoFrame;
  suppressBlur: boolean;
  layoutH: "left" | "center" | "right";
  layoutV: "top" | "center" | "bottom";
}

function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, n));
}

function vdbg(...args: any[]) {
  if (process.env.NODE_ENV !== "production") {
    console.debug("[layoutVideo]", ...args);
  }
}

export function videoZ(layer: "video" | "blur" | "gradient" | "content" | "hud"): number {
  switch (layer) {
    case "hud": return 900;
    case "gradient": return 200;
    case "blur": return 100;
    case "content": return 500;
    case "video": return 1;
  }
}

export function computeVideoFrame(
  section: HTMLElement,
  video: HTMLVideoElement,
  attrs: Record<string, any>
): VideoLayoutResult {
  const isReel = String(attrs["is-reel"] || "").toLowerCase() === "1";
  const screenPortrait = window.innerHeight > window.innerWidth;
  const videoPortrait = video.videoHeight > video.videoWidth;

  let layoutH = String(attrs["video-layout-h"] ?? "center")
    .toLowerCase()
    .replace(/-(fill|crop)$/i, "") as "left" | "center" | "right";

  let rawV = String(attrs["video-layout-v"] ?? "center").toLowerCase();
  if (rawV === "middle") rawV = "center";  // alias middle → center
  let layoutV = rawV as "top" | "center" | "bottom";

  if (!["left", "center", "right"].includes(layoutH)) layoutH = "center";
  if (!["top", "center", "bottom"].includes(layoutV)) layoutV = "center";

  const defaultSidePct = isReel ? 45 : 50;
  const sidePct = clamp(parseInt(String(attrs["video-side-pct"] ?? `${defaultSidePct}`), 10) || defaultSidePct, 20, 70);
  const restPct = 100 - sidePct;

  const vertPct = clamp(parseInt(String(attrs["video-vert-pct"] ?? "56"), 10) || 56, 30, 80);
  const remVertPct = 100 - vertPct;

  const hasBackgroundH = !!attrs["background-h"];
  const hasBackgroundV = !!attrs["background-v"];
  const suppressOverride = String(attrs["suppress-canvas"] ?? "").toLowerCase() === "true";
  const forceCanvas = String(attrs["force-canvas"] ?? "").toLowerCase() === "true";

  let frame: VideoFrame = { left: "0", top: "0", width: "100%", height: "100%" };
  let textPanel: VideoFrame = { ...frame };
  let canvasBox: VideoFrame = { left: "0", top: "0", width: "100%", height: "100%" };

  let suppressBlur = false;

  if (screenPortrait) {
    // ===== Portrait Screen =====
    if (videoPortrait) {
      frame = { left: "0", top: "0", width: "100%", height: "100%" };
      textPanel = { ...frame };
      suppressBlur = true;
    } else {
      // Landscape video on portrait screen: fill the frame and use layoutV to anchor vertically.
      // Horizontal cropping is driven by `video-crop`/layoutH later via objectPosition.
      frame = { left: "0", top: "0", width: "100%", height: "100%" };
      textPanel = { ...frame };
      // Keep blur unless an authored background or explicit suppress flag says otherwise.
      suppressBlur = hasBackgroundH || hasBackgroundV || suppressOverride;
      if (forceCanvas) suppressBlur = false;
    }
  } else {
    // ===== Landscape Screen =====
    if (!videoPortrait) {
      // Landscape video on landscape screen
      switch (layoutH) {
        case "left":
          frame = { left: "0", top: "0", width: `${sidePct}vw`, height: "100%" };
          textPanel = { left: `${sidePct}vw`, top: "0", width: `${restPct}vw`, height: "100%" };
          break;
        case "right":
          frame = { left: `${restPct}vw`, top: "0", width: `${sidePct}vw`, height: "100%" };
          textPanel = { left: "0", top: "0", width: `${restPct}vw`, height: "100%" };
          break;
        case "center":
        default:
          // NEW LOGIC: center means full height video, centered horizontally; text overlays
          frame = { left: "0", top: "0", width: "100%", height: "100%" };
          textPanel = { left: "0", top: "0", width: "100%", height: "100%" };
          break;
      }
      suppressBlur = true;
      if (forceCanvas) suppressBlur = false;
    } else {
      // Portrait video on landscape screen
      const screenW = window.innerWidth || 1920;
      const screenH = window.innerHeight || 1080;
      const hasDims = video.videoWidth > 0 && video.videoHeight > 0;
      const ratio = hasDims ? video.videoWidth / video.videoHeight : NaN;

      const applyFallback = () => {
        switch (layoutH) {
          case "left":
            frame = { left: "0", top: "0", width: `${sidePct}vw`, height: "100%" };
            textPanel = { left: `${sidePct}vw`, top: "0", width: `${restPct}vw`, height: "100%" };
            break;
          case "right":
            frame = { left: `${restPct}vw`, top: "0", width: `${sidePct}vw`, height: "100%" };
            textPanel = { left: "0", top: "0", width: `${restPct}vw`, height: "100%" };
            break;
          case "center":
          default:
            // video full height/width; text overlays
            frame = { left: "0", top: "0", width: "100%", height: "100%" };
            textPanel = { left: "0", top: "0", width: "100%", height: "100%" };
            break;
        }
      };

      // If we don't have usable dimensions or we're in center layout, keep prior behavior
      if (!hasDims || !isFinite(ratio) || layoutH === "center") {
        applyFallback();
      } else {
        // Let the video hug full height; derive its width from the aspect ratio.
        const rawVideoW = screenH * ratio;
        const videoW = clamp(rawVideoW, 0, screenW);
        const textW = clamp(screenW - videoW, 0, screenW);

        const videoPct = screenW > 0 ? (videoW / screenW) * 100 : defaultSidePct;
        const textPct = screenW > 0 ? (textW / screenW) * 100 : 100 - videoPct;

        if (layoutH === "left") {
          frame = { left: "0", top: "0", width: `${videoPct}vw`, height: "100%" };
          textPanel = { left: `${videoPct}vw`, top: "0", width: `${textPct}vw`, height: "100%" };
        } else if (layoutH === "right") {
          frame = { left: `${textPct}vw`, top: "0", width: `${videoPct}vw`, height: "100%" };
          textPanel = { left: "0", top: "0", width: `${textPct}vw`, height: "100%" };
        } else {
          applyFallback();
        }
      }

      suppressBlur = false;
      if (hasBackgroundH || hasBackgroundV || suppressOverride) suppressBlur = true;
      if (forceCanvas) suppressBlur = false;
    }
  }

  canvasBox = { left: "0", top: "0", width: "100%", height: "100%" };

  return { frame, textPanel, canvasBox, suppressBlur, layoutH, layoutV };
}

export function updateVideoLayout(
  section: HTMLElement,
  video: HTMLVideoElement,
  attrs: Record<string, any>,
  frameEl?: HTMLElement | null,
  canvasEl?: HTMLElement | null
): VideoLayoutResult {
  const res = computeVideoFrame(section, video, attrs);

  // Ensure or create a frame wrapper we can use to clip the video when needed
  let frame = frameEl || (section.querySelector('.sc-video-frame') as HTMLElement | null);
  if (!frame) {
    frame = document.createElement('div');
    frame.className = 'sc-video-frame';
    section.appendChild(frame);
  }

  Object.assign(video.style, {
    position: "absolute",
    left: res.frame.left,
    top: res.frame.top,
    width: res.frame.width,
    height: res.frame.height,
    zIndex: String(videoZ("video")),
    objectFit: "cover",
    objectPosition: `${res.layoutH} ${res.layoutV}`,
    backgroundColor: normalizeColor(String(attrs["background-color"] || "transparent")),
  });

  // Special handling: portrait video on landscape screen with left/right layout
  const isLandscapeScreen = window.innerWidth >= window.innerHeight;
  const isPortraitVideo = video.videoHeight > video.videoWidth;

  // Special handling: portrait video on landscape screen with center layout
  if (isLandscapeScreen && isPortraitVideo && res.layoutH === 'center') {
    // If the video is currently inside the frame wrapper from a previous layout, move it back to the section
    if (frame && video.parentElement === frame) {
      section.appendChild(video);
    }
    // Keep the frame around (for future left/right layouts), but make it inert
    if (frame) {
      Object.assign(frame.style, {
        width: '0',
        height: '0',
        overflow: 'visible',
      });
    }

    // Portrait video should hug full height, auto width, centered horizontally
    Object.assign(video.style, {
      position: 'absolute',
      top: '0',
      left: '50%',
      transform: 'translateX(-50%)',
      height: '100%',
      width: 'auto',
      right: 'auto',
      bottom: '0',
      // `contain` preserves the whole portrait video; gutters are handled by the blur canvas
      objectFit: 'contain',
      objectPosition: `${res.layoutH} ${res.layoutV}`,
    });
  } else if (isLandscapeScreen && isPortraitVideo && (res.layoutH === 'left' || res.layoutH === 'right')) {
    // Position the frame box according to computed geometry and clip overflow
    if (frame) {
      Object.assign(frame.style, {
        position: 'absolute',
        left: res.frame.left,
        top: res.frame.top,
        width: res.frame.width,
        height: res.frame.height,
        overflow: 'hidden',
        zIndex: String(videoZ('video')),
        pointerEvents: 'none',
      });

      // Make sure the video is inside the frame for clipping
      if (video.parentElement !== frame) {
        frame.appendChild(video);
      }

      // Video should fill height, auto width, and hug the chosen side
      Object.assign(video.style, {
        position: 'absolute',
        height: '100%',
        width: 'auto',
        left: res.layoutH === 'left' ? '0' : 'auto',
        right: res.layoutH === 'right' ? '0' : 'auto',
        top: '0',
        bottom: '0',
        objectFit: 'cover', // safe; width is auto so cover behaves as expected
        objectPosition: `${res.layoutH} ${res.layoutV}`,
      });
    }
  } else {
    // Default path: no special wrapper clipping required. If video is inside the frame, move it back.
    if (frame && video.parentElement === frame) {
      section.appendChild(video);
    }
    if (frame) {
      // Keep frame around for future layouts but hide it
      frame.style.width = '0';
      frame.style.height = '0';
      frame.style.overflow = 'visible';
    }
  }

  if (canvasEl) {
    Object.assign(canvasEl.style, {
      position: "absolute",
      left: res.canvasBox.left,
      top: res.canvasBox.top,
      width: res.canvasBox.width,
      height: res.canvasBox.height,
      zIndex: String(videoZ("blur")),
      pointerEvents: "none",
    });
  }

  const container = section.querySelector(".container") as HTMLElement | null;
  if (container) {
    Object.assign(container.style, {
      position: "absolute",
      left: res.textPanel.left,
      top: res.textPanel.top,
      width: res.textPanel.width,
      height: res.textPanel.height,
      zIndex: String(videoZ("content")),
      overflow: "hidden",
    });
  }

  return res;
}

export function applyVideoLayout(
  section: HTMLElement,
  video: HTMLVideoElement,
  attrs: Record<string, any>,
  frameEl?: HTMLElement | null,
  canvasEl?: HTMLElement | null
): VideoLayoutResult {
  const res = updateVideoLayout(section, video, attrs, frameEl, canvasEl);

  // Patch: support video-crop for fine-grained control of visible portion of the video.
  // Find and update all objectPosition assignments to allow `video-crop:` override.
  // This is the main place for the default video style:
  if (video && video.style) {
    Object.assign(video.style, {
      // Allow authors to override visible portion of video via `video-crop:` (accepts "left center", "center-left", "20% 50%", etc.)
      objectPosition: (attrs["video-crop"]
        ? String(attrs["video-crop"]).replace("-", " ")
        : `${res.layoutH} ${res.layoutV}`),
    });
  }

  try {
    import("../render/videoBlurCanvas").then(({ setupVideoCanvas }) => {
      try {
        setupVideoCanvas(section, video, {
          source: String(attrs["video"] ?? attrs["video-h"] ?? attrs["video-v"] ?? ""),
          effectiveLayout: res.layoutH,
          isVerticalVideo: video.videoHeight > video.videoWidth,
          isVerticalScreen: window.innerHeight > window.innerWidth,
        });
      } catch (err) {
        vdbg("setupVideoCanvas failed:", err);
      }
    });
  } catch (e) {
    vdbg("dynamic import (videoCanvas) failed:", e);
  }

  return res;
}
